import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer } from "lucide-react";

export default function InvoicePrint() {
  const { bookingId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/admin/bookings/${bookingId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
  }, [bookingId]);

  if (err) return <div className="pt-24 text-center text-red-600" data-testid="invoice-print-error">{err}</div>;
  if (!data) return <div className="pt-24 text-center text-slate-500">Loading…</div>;
  const b = data.booking;
  const inv = data.invoice;
  const est = (data.estimates || []).slice(-1)[0];
  if (!inv) return <div className="pt-24 text-center text-slate-500">No invoice has been generated for this booking yet.</div>;
  const biz = inv.business_snapshot || {};
  const w = inv.warranty_snapshot || {};
  const partsPaid = inv.parts_paid_online ? (inv.parts_total ?? 0) : 0;
  const balance = Math.max(0, (inv.grand_total ?? 0) - partsPaid);

  return (
    <div className="pt-24 pb-16 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 print-page" data-testid="invoice-print">
      <div className="no-print flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-slate-900">Invoice preview</h1>
        <button data-testid="print-btn" onClick={() => window.print()} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">
          <Printer className="w-4 h-4" /> Print / Save as PDF
        </button>
      </div>

      <div className="border border-slate-300 rounded-2xl bg-white p-8 print-page">
        <div className="flex items-baseline justify-between border-b border-slate-300 pb-3 mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Final Invoice</div>
            <h2 className="text-2xl font-bold text-slate-900 mt-1">{biz.legal_business_name || "The Repair Nook"}</h2>
            <div className="text-xs text-slate-600 mt-1 whitespace-pre-line">{biz.address || ""}</div>
            <div className="text-xs text-slate-600">{biz.phone || ""} · {biz.email || ""}</div>
            {biz.ca_esd_registration && <div className="text-xs text-slate-600">California ESD Reg. No. {biz.ca_esd_registration}</div>}
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-slate-500">Invoice #</div>
            <div className="font-mono font-bold text-lg text-slate-900" data-testid="invoice-number">{inv.invoice_number}</div>
            {inv.claim_number && <div className="text-[10px] text-slate-500 mt-1">Claim {inv.claim_number}</div>}
            <div className="text-[10px] text-slate-500">Issued {new Date(inv.created_at).toLocaleDateString()}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 text-xs mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Bill to</div>
            <div className="text-slate-900 font-semibold">{inv.customer?.name}</div>
            <div className="text-slate-700">{inv.customer?.street}</div>
            <div className="text-slate-700">{inv.customer?.city}{inv.customer?.city ? "," : ""} {inv.customer?.state} {inv.customer?.zip}</div>
            <div className="text-slate-700">{inv.customer?.phone}</div>
            <div className="text-slate-700">{inv.customer?.email}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Device</div>
            <div className="text-slate-900">{inv.device?.manufacturer} {inv.device?.model_name}</div>
          </div>
        </div>

        {est && (
          <div className="text-xs mb-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Diagnosis</div>
            <div className="text-slate-800">{est.diagnosis}</div>
          </div>
        )}

        <table className="w-full text-xs border border-slate-200 rounded overflow-hidden mb-4">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left p-2 font-semibold text-slate-700">Item</th>
              <th className="text-left p-2 font-semibold text-slate-700">Part quality</th>
              <th className="text-right p-2 font-semibold text-slate-700">Parts</th>
              <th className="text-right p-2 font-semibold text-slate-700">Labor</th>
            </tr>
          </thead>
          <tbody>
            {est?.lines?.map((l, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="p-2">{l.label}</td>
                <td className="p-2 text-slate-500">{l.part_quality || "—"}</td>
                <td className="p-2 text-right font-mono">${(l.parts_cost || 0).toFixed(2)}</td>
                <td className="p-2 text-right font-mono">${(l.labor_cost || 0).toFixed(2)}</td>
              </tr>
            ))}
            {(!est?.lines || est.lines.length === 0) && (
              <tr><td colSpan={4} className="p-2 text-slate-400 text-center">No itemized lines available</td></tr>
            )}
          </tbody>
        </table>

        <div className="ml-auto max-w-xs text-xs space-y-1 mb-4">
          <div className="flex justify-between"><span>Parts total</span><span className="font-mono">${(inv.parts_total ?? 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span>Labor total</span><span className="font-mono">${(inv.labor_total ?? 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span>Sales tax</span><span className="font-mono">${(inv.tax_total ?? 0).toFixed(2)}</span></div>
          <div className="flex justify-between font-bold border-t border-slate-300 pt-1"><span>Grand total</span><span className="font-mono">${(inv.grand_total ?? 0).toFixed(2)}</span></div>
          <div className="flex justify-between text-slate-500"><span>Less parts paid online</span><span className="font-mono">−${partsPaid.toFixed(2)}</span></div>
          <div className="flex justify-between text-lg font-bold text-amber-800 border-t border-amber-300 pt-1"><span>Balance due at pickup</span><span className="font-mono" data-testid="balance-due">${balance.toFixed(2)}</span></div>
        </div>

        <div className="text-[11px] text-slate-600 border-t border-slate-200 pt-3" data-testid="invoice-ca-notice-block">
          <div className="uppercase tracking-widest text-[9px] font-semibold text-slate-500 mb-1">California statutory notice</div>
          {inv.ca_notice_version ? (
            <div className="p-3 rounded border border-amber-200 bg-amber-50 text-amber-900 whitespace-pre-line leading-relaxed" data-testid="invoice-ca-notice">{inv.ca_notice_version}</div>
          ) : (
            <div className="p-3 rounded border border-red-200 bg-red-50 text-red-800" data-testid="invoice-ca-notice-missing">
              California statutory notice not configured in Admin → Business Settings. Owner action required before this invoice is delivered to a customer.
            </div>
          )}
        </div>

        <div className="text-[11px] text-slate-600 border-t border-slate-200 pt-3 mt-3">
          <div className="uppercase tracking-widest text-[9px] font-semibold text-slate-500 mb-1">Warranty</div>
          <div>{(w.days || 90)}-day limited repair warranty.</div>
          {w.covered && <div className="mt-1"><strong>Covered:</strong> {w.covered}</div>}
          {w.excluded && <div className="mt-1"><strong>Not covered:</strong> {w.excluded}</div>}
          {w.remedy && <div className="mt-1"><strong>Remedy:</strong> {w.remedy}</div>}
        </div>

        <div className="mt-4 text-[11px] text-slate-600">
          Technician: {inv.technician}
        </div>
      </div>
    </div>
  );
}
